import { SearchInput } from '@/catalog-apps/components/component-library/shared/SearchInput'
import { Button } from '@/components/shadcn/button'
import { ScrollArea } from '@/components/shadcn/scroll-area'
import { Skeleton } from '@/components/shadcn/skeleton'
import { NoSearchResultsDisplay } from '@/components/ui/NoSeachResultsDisplay'
import { useSessionStorage } from '@/hooks'
import { OrgProjectForm } from '@/pages/Subscriptions/Forms/OrgProjectForm'
import { RequestProjectCreateForm } from '@/pages/Subscriptions/Forms/RequestProjectCreateForm'
import { IconPlusOutline } from '@icons/IconPlusOutline'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/shadcn/dropdown-menu'
import { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react'
import { projectOrWorkspaceMatchesSearch, hasWorkspaceOnlySearchMatch } from '../projectWorkspaceUtils'
import {
  getOrganisationPermissions,
  getProjectOrganisationId,
  useUserLinkedOrganisations,
  useUserLinkedProjects,
  UserLinkedOrganisation,
} from '../useUserLinkedProjects'
import { ProjectsDetailPanel } from './ProjectsDetailPanel'
import { getProjectsSidebarItemId, ProjectsSidebarItem } from './ProjectsSidebarItem'

function ProjectActions({
  organisation,
  onProjectCreated,
}: {
  organisation: UserLinkedOrganisation
  onProjectCreated: (projectId: number) => void
}) {
  const requestProjectForm = (trigger: React.ReactNode) => (
    <RequestProjectCreateForm
      organisationId={organisation.organisationId}
      sheetTitle={`${organisation.name} > Request project`}
      sheetDescription={`Request a new project in ${organisation.name}.`}
      sheetTrigger={trigger}
    />
  )

  if (!organisation.canAddProjects) {
    return requestProjectForm(
      <Button variant="outline" size="icon">
        <IconPlusOutline className="size-5" />
        <span className="sr-only">Request project in {organisation.name}</span>
      </Button>
    )
  }

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant="ghost" size="icon">
          <IconPlusOutline className="size-4" />
          <span className="sr-only">Add or request a project in {organisation.name}</span>
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end">
        <OrgProjectForm
          organisationId={organisation.organisationId}
          onProjectCreated={onProjectCreated}
          sheetTitle="Create a new project"
          sheetDescription={`Create a new project in ${organisation.name}.`}
          sheetTrigger={<DropdownMenuItem onSelect={(event) => event.preventDefault()}>Add a project</DropdownMenuItem>}
        />
        {requestProjectForm(
          <DropdownMenuItem onSelect={(event) => event.preventDefault()}>Request a project</DropdownMenuItem>
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  )
}

export function ProjectsGrid({
  organisationFilter,
  activeOrganisation,
  onProjectCreated,
}: {
  organisationFilter: string | null
  activeOrganisation?: UserLinkedOrganisation
  onProjectCreated: (projectId: number) => void
}) {
  const projects = useUserLinkedProjects()
  const organisations = useUserLinkedOrganisations()
  const [searchString, setSearchString] = useState('')
  const [selectedProjectId, setSelectedProjectId] = useSessionStorage<number | null>(
    'projectsHomeSelectedProjectId',
    null
  )
  const hasScrolledToSelectedRef = useRef(false)

  const filteredProjectIds = useMemo(() => {
    const search = searchString.trim().toLowerCase()

    return projects
      .filter((project) => {
        if (organisationFilter && project.organisation !== organisationFilter) {
          return false
        }

        return projectOrWorkspaceMatchesSearch(project, search)
      })
      .map((project) => project.projectId)
  }, [organisationFilter, projects, searchString])

  const sortedProjects = useMemo(() => [...projects].sort((a, b) => a.name.localeCompare(b.name)), [projects])

  const visibleProjects = useMemo(
    () => sortedProjects.filter((project) => filteredProjectIds.includes(project.projectId)),
    [filteredProjectIds, sortedProjects]
  )

  const selectedProject = useMemo(
    () => visibleProjects.find((project) => project.projectId === selectedProjectId),
    [visibleProjects, selectedProjectId]
  )

  useEffect(() => {
    const search = searchString.trim().toLowerCase()

    if (visibleProjects.length === 0) {
      setSelectedProjectId(null)
      return
    }

    if (search) {
      const workspaceMatch = visibleProjects.find((project) => hasWorkspaceOnlySearchMatch(project, search))
      if (workspaceMatch) {
        setSelectedProjectId(workspaceMatch.projectId)
        return
      }
    }

    if (selectedProjectId != null && visibleProjects.some((project) => project.projectId === selectedProjectId)) {
      return
    }

    setSelectedProjectId(visibleProjects[0].projectId)
  }, [visibleProjects, searchString, selectedProjectId, setSelectedProjectId])

  useLayoutEffect(() => {
    if (hasScrolledToSelectedRef.current || selectedProjectId == null) {
      return
    }

    if (!visibleProjects.some((project) => project.projectId === selectedProjectId)) {
      return
    }

    const scrollToSelected = () => {
      const element = document.getElementById(getProjectsSidebarItemId(selectedProjectId))
      if (!element) {
        return false
      }

      element.scrollIntoView({ block: 'nearest' })
      hasScrolledToSelectedRef.current = true
      return true
    }

    if (scrollToSelected()) {
      return
    }

    requestAnimationFrame(() => {
      scrollToSelected()
    })
  }, [selectedProjectId, visibleProjects])

  const selectedOrganisationId = selectedProject ? getProjectOrganisationId(selectedProject) : undefined
  const selectedOrgPermissions = getOrganisationPermissions(organisations, selectedOrganisationId)

  return (
    <div className="flex h-full min-h-0">
      {/* <aside className="flex w-80 shrink-0 flex-col border-r border-border"> */}
      <aside className="flex w-[340px] shrink-0 flex-col border-r">
        <div className="flex h-14 shrink-0 items-center gap-2 border-b border-border px-3">
          <SearchInput value={searchString} onChange={setSearchString} className="min-w-0 flex-1" />
          {activeOrganisation != null && (
            <ProjectActions organisation={activeOrganisation} onProjectCreated={onProjectCreated} />
          )}
        </div>
        {visibleProjects.length > 0 ? (
          <ScrollArea className="min-h-0 flex-1">
            <div className="py-2 pr-2">
              {visibleProjects.map((project) => (
                <ProjectsSidebarItem
                  key={project.projectId}
                  project={project}
                  isSelected={project.projectId === selectedProjectId}
                  onSelect={setSelectedProjectId}
                />
              ))}
            </div>
          </ScrollArea>
        ) : (
          <NoSearchResultsDisplay />
        )}
      </aside>
      <main className="min-h-0 min-w-0 flex-1">
        {selectedProject ? (
          <ProjectsDetailPanel
            project={selectedProject}
            searchString={searchString}
            canEditProjectLeaders={selectedOrgPermissions?.canEditProjectLeaders ?? false}
          />
        ) : (
          <p className="px-5 py-8 text-muted-foreground">No projects match your search.</p>
        )}
      </main>
    </div>
  )
}

export function ProjectsGridLoading() {
  return (
    <div className="flex h-full min-h-0">
      <aside className="flex w-80 shrink-0 flex-col border-r border-border">
        <div className="flex h-14 shrink-0 items-center gap-2 border-b border-border px-3">
          <Skeleton className="h-9 min-w-0 flex-1" />
          <Skeleton className="size-9 shrink-0" />
        </div>
        <div className="flex flex-col gap-0 p-0">
          {Array.from({ length: 8 }).map((_, index) => (
            <Skeleton key={index} className="h-20 w-full rounded-none" />
          ))}
        </div>
      </aside>
      <main className="min-h-0 min-w-0 flex-1">
        <Skeleton className="h-14 w-full rounded-none border-b border-border" />
        <Skeleton className="h-full min-h-[400px] w-full" />
      </main>
    </div>
  )
}
